/**
 * ro-timeline.js — the strip under the stage of /remove-object-from-video.
 *
 * Three layers in one control, because they answer one question — "where in
 * the clip is the thing I am removing?":
 *
 *   · FILM   — thumbnails, filled while the clip is read. The frames are
 *              decoded for tracking anyway; drawing a 64-px copy of each is
 *              free, and it turns the wait into something to look at.
 *   · TRACKS — one bar per marked object, in the object's colour, from where
 *              it was first seen to where it was last seen, with a notch where
 *              the tracker lost it. Before this the only place that fact lived
 *              was a sentence under the video ("отслежен до 0:21").
 *   · HEAD   — the playhead; the strip is also the scrubber (pointer, touch
 *              and keyboard), so there is one timeline, not a range input plus
 *              a picture of one.
 *
 * window.RoTimeline.create(opts) → api
 *   opts: { el, film, tracks, head, onSeek(t, phase), onStep(dir, big) }
 *   api:  setDuration(s) · setAspect(w/h) · addThumb(t, canvas) · clearThumbs()
 *         setTracks([{color, start, end, lostT, lostDir, active}]) · setTime(t)
 *         resize() · destroy()
 */
(function (root) {
    'use strict';

    var DPR_CAP = 2;
    var THUMB_H = 40;          // stored thumbnail height (css px × dpr cap)
    var MAX_THUMBS = 480;

    function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }

    function create(opts) {
        var el = opts.el, film = opts.film, tracks = opts.tracks, head = opts.head;
        var fctx = film.getContext('2d');
        var tctx = tracks.getContext('2d');
        var dur = 0, aspect = 16 / 9, t = 0;
        var thumbs = [];           // [{t, c: canvas}] sorted by t
        var lanes = [];
        var dragging = false, pid = null;
        var W = 0, H = 0, dpr = 1;
        var raf = 0;

        function measure() {
            var r = el.getBoundingClientRect();
            dpr = Math.min(DPR_CAP, root.devicePixelRatio || 1);
            W = Math.max(1, Math.round(r.width));
            H = Math.max(1, Math.round(r.height));
            [film, tracks].forEach(function (c) {
                var w = Math.round(W * dpr), h = Math.round(H * dpr);
                if (c.width !== w) c.width = w;
                if (c.height !== h) c.height = h;
            });
        }

        function schedule() {
            if (raf) return;
            raf = (root.requestAnimationFrame || setTimeout)(function () { raf = 0; drawFilm(); drawTracks(); });
        }

        function nearestThumb(tt) {
            if (!thumbs.length) return null;
            var lo = 0, hi = thumbs.length - 1;
            while (lo < hi) {
                var mid = (lo + hi) >> 1;
                if (thumbs[mid].t < tt) lo = mid + 1; else hi = mid;
            }
            var best = thumbs[lo];
            if (lo > 0 && Math.abs(thumbs[lo - 1].t - tt) < Math.abs(best.t - tt)) best = thumbs[lo - 1];
            return best;
        }

        /* Film fills the strip above the track lane. Slots are as wide as a
           thumbnail of the clip's own aspect at the strip's height, so a phone
           clip shows many narrow frames and a landscape one a few wide ones —
           the strip reads as the video, not as a row of letterboxes. */
        function drawFilm() {
            var w = film.width, h = film.height;
            fctx.clearRect(0, 0, w, h);
            if (!dur) return;
            var filmH = Math.round(h * 0.72);
            var slotW = Math.max(8, Math.round(filmH * aspect));
            var n = Math.max(1, Math.ceil(w / slotW));
            slotW = w / n;
            for (var i = 0; i < n; i++) {
                var tc = (i + 0.5) / n * dur;
                var th = nearestThumb(tc);
                var x = Math.floor(i * slotW), sw = Math.ceil(slotW);
                if (th && Math.abs(th.t - tc) <= Math.max(0.6, dur / n)) {
                    // cover-fit the thumbnail into the slot
                    var sc = Math.max(sw / th.c.width, filmH / th.c.height);
                    var dw = th.c.width * sc, dh = th.c.height * sc;
                    fctx.save();
                    fctx.beginPath(); fctx.rect(x, 0, sw, filmH); fctx.clip();
                    fctx.drawImage(th.c, x + (sw - dw) / 2, (filmH - dh) / 2, dw, dh);
                    fctx.restore();
                } else {
                    fctx.fillStyle = (i % 2) ? '#141c2b' : '#101725';
                    fctx.fillRect(x, 0, sw, filmH);
                }
            }
            // hairline separators keep the strip from reading as one smear
            fctx.fillStyle = 'rgba(0,0,0,.35)';
            for (var s = 1; s < n; s++) fctx.fillRect(Math.round(s * slotW), 0, Math.max(1, dpr * 0.6), filmH);
        }

        function drawTracks() {
            var w = tracks.width, h = tracks.height;
            tctx.clearRect(0, 0, w, h);
            if (!dur) return;
            var filmH = Math.round(h * 0.72);
            var laneTop = filmH, laneH = h - filmH;
            tctx.fillStyle = '#0b111c';
            tctx.fillRect(0, laneTop, w, laneH);
            if (!lanes.length) return;
            var n = lanes.length;
            var gap = Math.max(1, Math.round(dpr));
            var each = Math.max(2, Math.floor((laneH - gap * (n + 1)) / n));
            for (var i = 0; i < n; i++) {
                var L = lanes[i];
                var y = laneTop + gap + i * (each + gap);
                var x0 = Math.round(clamp(L.start / dur, 0, 1) * w);
                var x1 = Math.round(clamp(L.end / dur, 0, 1) * w);
                tctx.globalAlpha = L.active ? 1 : 0.6;
                tctx.fillStyle = L.color;
                tctx.fillRect(x0, y, Math.max(2 * dpr, x1 - x0), each);
                // the object also shows ON the film: a tint over the frames where it is tracked
                tctx.globalAlpha = L.active ? 0.22 : 0.1;
                tctx.fillRect(x0, 0, Math.max(2 * dpr, x1 - x0), filmH);
                tctx.globalAlpha = 1;
                if (L.lostT != null && isFinite(L.lostT)) {
                    var lx = Math.round(clamp(L.lostT / dur, 0, 1) * w);
                    tctx.fillStyle = '#fbbf24';
                    tctx.beginPath();
                    tctx.moveTo(lx, filmH - 1);
                    tctx.lineTo(lx - 5 * dpr, filmH - 9 * dpr);
                    tctx.lineTo(lx + 5 * dpr, filmH - 9 * dpr);
                    tctx.closePath();
                    tctx.fill();
                }
            }
        }

        function placeHead() {
            if (!head) return;
            var x = dur ? clamp(t / dur, 0, 1) * W : 0;
            head.style.transform = 'translateX(' + x.toFixed(1) + 'px)';
            el.setAttribute('aria-valuenow', dur ? String(Math.round(t / dur * 100)) : '0');
            el.setAttribute('aria-valuetext', fmt(t) + ' / ' + fmt(dur));
        }

        function fmt(s) {
            s = Math.max(0, s || 0);
            var m = Math.floor(s / 60), r = Math.floor(s - m * 60);
            return m + ':' + (r < 10 ? '0' : '') + r;
        }

        function timeAt(clientX) {
            var r = el.getBoundingClientRect();
            return clamp((clientX - r.left) / Math.max(1, r.width), 0, 1) * dur;
        }

        /* A finger on the strip has to declare itself first. On a phone the
           strip is a full-width band under the stage, and a swipe that merely
           scrolled the page across it used to jump the video to wherever the
           finger landed (prod 13–15.09: seeks in 91 of 159 phone sessions,
           abandons ending on a seek). The strip now lets the browser own
           vertical panning (touch-action:pan-y, which ends our gesture with
           pointercancel); a HORIZONTAL move scrubs, and a finger that lifts
           where it landed is a tap that seeks. A mouse seeks on press, as it did. */
        var touch = null;
        var TOUCH_SLOP = 6, TAP_SLOP = 10;
        function onDown(ev) {
            if (!dur || (ev.button != null && ev.button !== 0)) return;
            if (ev.pointerType === 'touch' || ev.pointerType === 'pen') {
                touch = { id: ev.pointerId, x: ev.clientX, y: ev.clientY, lastX: ev.clientX, scrubbing: false };
                return;
            }
            dragging = true; pid = ev.pointerId;
            try { el.setPointerCapture(pid); } catch (_) { }
            opts.onSeek && opts.onSeek(timeAt(ev.clientX), 'start');
            ev.preventDefault();
        }
        function onMove(ev) {
            if (touch && ev.pointerId === touch.id) {
                touch.lastX = ev.clientX;
                if (!touch.scrubbing) {
                    var dx = Math.abs(ev.clientX - touch.x), dy = Math.abs(ev.clientY - touch.y);
                    if (dx < TOUCH_SLOP || dx <= dy) return;
                    touch.scrubbing = true; dragging = true; pid = ev.pointerId;
                    try { el.setPointerCapture(pid); } catch (_) { }
                    opts.onSeek && opts.onSeek(timeAt(touch.x), 'start');
                }
                opts.onSeek && opts.onSeek(timeAt(ev.clientX), 'move');
                return;
            }
            if (!dragging || ev.pointerId !== pid) return;
            opts.onSeek && opts.onSeek(timeAt(ev.clientX), 'move');
        }
        function onUp(ev) {
            if (touch && ev.pointerId === touch.id) {
                var g = touch; touch = null;
                if (g.scrubbing) {
                    dragging = false;
                    try { el.releasePointerCapture(g.id); } catch (_) { }
                    opts.onSeek && opts.onSeek(timeAt(ev.type === 'pointercancel' ? g.lastX : ev.clientX), 'end');
                    return;
                }
                // The browser took the finger for a scroll: nothing happened here.
                if (ev.type === 'pointercancel') return;
                if (Math.max(Math.abs(ev.clientX - g.x), Math.abs(ev.clientY - g.y)) > TAP_SLOP) return;
                opts.onSeek && opts.onSeek(timeAt(ev.clientX), 'start');
                opts.onSeek && opts.onSeek(timeAt(ev.clientX), 'end');
                return;
            }
            if (!dragging || ev.pointerId !== pid) return;
            dragging = false;
            try { el.releasePointerCapture(pid); } catch (_) { }
            opts.onSeek && opts.onSeek(timeAt(ev.clientX), 'end');
        }
        function onKey(ev) {
            var k = ev.key;
            if (k === 'ArrowLeft' || k === 'ArrowRight') {
                opts.onStep && opts.onStep(k === 'ArrowLeft' ? -1 : 1, !!ev.shiftKey);
                ev.preventDefault();
            } else if (k === 'Home' || k === 'End') {
                opts.onSeek && opts.onSeek(k === 'Home' ? 0 : dur, 'end');
                ev.preventDefault();
            }
        }

        el.addEventListener('pointerdown', onDown);
        el.addEventListener('pointermove', onMove);
        el.addEventListener('pointerup', onUp);
        el.addEventListener('pointercancel', onUp);
        el.addEventListener('keydown', onKey);
        var ro = null;
        if (root.ResizeObserver) { ro = new ResizeObserver(function () { measure(); schedule(); placeHead(); }); ro.observe(el); }
        measure();

        return {
            setDuration: function (s) { dur = Math.max(0, +s || 0); schedule(); placeHead(); },
            setAspect: function (a) { if (a > 0 && isFinite(a)) { aspect = a; schedule(); } },
            addThumb: function (tt, src) {
                if (thumbs.length >= MAX_THUMBS || !src || !src.width) return;
                var hh = Math.round(THUMB_H * dpr);
                var ww = Math.max(1, Math.round(hh * src.width / src.height));
                var c = document.createElement('canvas');
                c.width = ww; c.height = hh;
                c.getContext('2d').drawImage(src, 0, 0, ww, hh);
                var item = { t: tt, c: c };
                if (!thumbs.length || thumbs[thumbs.length - 1].t <= tt) thumbs.push(item);
                else { thumbs.push(item); thumbs.sort(function (a, b) { return a.t - b.t; }); }
                schedule();
            },
            clearThumbs: function () { thumbs = []; lanes = []; schedule(); },
            setTracks: function (list) { lanes = Array.isArray(list) ? list.slice(0, 6) : []; schedule(); },
            setTime: function (s) { t = +s || 0; placeHead(); },
            resize: function () { measure(); schedule(); placeHead(); },
            destroy: function () {
                el.removeEventListener('pointerdown', onDown);
                el.removeEventListener('pointermove', onMove);
                el.removeEventListener('pointerup', onUp);
                el.removeEventListener('pointercancel', onUp);
                el.removeEventListener('keydown', onKey);
                if (ro) ro.disconnect();
            },
            fmt: fmt
        };
    }

    root.RoTimeline = { create: create };
})(typeof window !== 'undefined' ? window : globalThis);
